/**
 * Entitlements client with OfficeRuntime.storage caching.
 * Normalizes API response to internal shape; callers use plan/status, not effective_*.
 */

/* global OfficeRuntime */

import { getEntitlementsMe } from "../proxyClient";
import { STORAGE_KEYS } from "../../shared/constants";
import { logger } from "../../shared/logger";
import type { EntitlementsLimits } from "../../shared/types";

const SKEW_MS = 45_000;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export interface Entitlements {
  plan: "free" | "pro";
  status: string;
  limits: EntitlementsLimits;
  updated_at: string;
  expires_at: string;
  current_period_end: string | null;
  request_id: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}

/** Match proxy FREE_LIMITS so formula and taskpane behave consistently when API fails. */
const CONSERVATIVE_LIMITS: EntitlementsLimits = {
  max_refresh_items_per_run: 25,
  confirm_threshold: 10,
  max_chain_rows: 10,
  max_requests_per_minute: 15,
};

function nowIso(): string {
  return new Date().toISOString();
}

function synthesizeFree(): Entitlements {
  const now = nowIso();
  const shortExpiry = new Date(Date.now() + 60 * 1000).toISOString();
  return {
    plan: "free",
    status: "anonymous",
    limits: { ...CONSERVATIVE_LIMITS },
    updated_at: now,
    expires_at: shortExpiry,
    current_period_end: null,
    request_id: "synthesized-free",
  };
}

function normalizeFromApi(api: {
  effective_plan: "free" | "pro";
  effective_status: string;
  limits: EntitlementsLimits;
  updated_at: string;
  expires_at: string;
  current_period_end: string | null;
  request_id: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}): Entitlements {
  return {
    plan: api.effective_plan,
    status: api.effective_status,
    limits: api.limits,
    updated_at: api.updated_at,
    expires_at: api.expires_at,
    current_period_end: api.current_period_end,
    request_id: api.request_id,
    stripe_customer_id: api.stripe_customer_id,
    stripe_subscription_id: api.stripe_subscription_id,
  };
}

let inflightForce: Promise<Entitlements> | null = null;
let inflightNonForce: Promise<Entitlements> | null = null;

function getStorage(): { getItem: (k: string) => Promise<string | null>; setItem: (k: string, v: string) => Promise<void>; removeItem: (k: string) => Promise<void> } | null {
  if (typeof OfficeRuntime !== "undefined" && OfficeRuntime?.storage) {
    return OfficeRuntime.storage as any;
  }
  return null;
}

export function isEntitlementsStale(entitlements: Entitlements): boolean {
  const expiresAt = new Date(entitlements.expires_at).getTime();
  return Date.now() >= expiresAt - SKEW_MS;
}

export async function getCachedEntitlements(): Promise<Entitlements | null> {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = await storage.getItem(STORAGE_KEYS.ENTITLEMENTS);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      !parsed ||
      typeof parsed.expires_at !== "string" ||
      !parsed.limits ||
      typeof parsed.limits !== "object"
    ) {
      await storage.removeItem(STORAGE_KEYS.ENTITLEMENTS);
      return null;
    }

    const limits = parsed.limits as EntitlementsLimits;
    if (
      typeof limits.max_refresh_items_per_run !== "number" ||
      typeof limits.confirm_threshold !== "number" ||
      typeof limits.max_chain_rows !== "number"
    ) {
      await storage.removeItem(STORAGE_KEYS.ENTITLEMENTS);
      return null;
    }

    const plan = (parsed.plan as "free" | "pro") || "free";
    // Normalize stale free-tier cache: never return free limits above current cap (CONSERVATIVE_LIMITS).
    const limitsToUse =
      plan === "free"
        ? {
            max_refresh_items_per_run: Math.min(
              limits.max_refresh_items_per_run,
              CONSERVATIVE_LIMITS.max_refresh_items_per_run
            ),
            confirm_threshold: Math.min(
              limits.confirm_threshold,
              CONSERVATIVE_LIMITS.confirm_threshold
            ),
            max_chain_rows: Math.min(limits.max_chain_rows, CONSERVATIVE_LIMITS.max_chain_rows),
            max_requests_per_minute: Math.min(
              limits.max_requests_per_minute,
              CONSERVATIVE_LIMITS.max_requests_per_minute
            ),
          }
        : limits;

    return {
      plan,
      status: (parsed.status as string) || "anonymous",
      limits: limitsToUse,
      updated_at: parsed.updated_at as string,
      expires_at: parsed.expires_at as string,
      current_period_end: parsed.current_period_end as string | null,
      request_id: (parsed.request_id as string) || "",
      stripe_customer_id: parsed.stripe_customer_id as string | null | undefined,
      stripe_subscription_id: parsed.stripe_subscription_id as string | null | undefined,
    };
  } catch {
    try {
      await storage.removeItem(STORAGE_KEYS.ENTITLEMENTS);
    } catch {
      /* ignore */
    }
    return null;
  }
}

async function storeEntitlements(ent: Entitlements): Promise<void> {
  const storage = getStorage();
  if (!storage) return;

  const payload = {
    plan: ent.plan,
    status: ent.status,
    limits: ent.limits,
    updated_at: ent.updated_at,
    expires_at: ent.expires_at,
    current_period_end: ent.current_period_end,
    request_id: ent.request_id,
    stripe_customer_id: ent.stripe_customer_id,
    stripe_subscription_id: ent.stripe_subscription_id,
  };
  await storage.setItem(STORAGE_KEYS.ENTITLEMENTS, JSON.stringify(payload));
}

/** Fallback when API omits limits. Match proxy FREE_LIMITS / PRO_LIMITS. */
function getLimitsFromPlan(plan: "free" | "pro"): EntitlementsLimits {
  return plan === "pro"
    ? {
        max_refresh_items_per_run: 500,
        confirm_threshold: 100,
        max_chain_rows: 500,
        max_requests_per_minute: 120,
      }
    : {
        max_refresh_items_per_run: 50,
        confirm_threshold: 10,
        max_chain_rows: 10,
        max_requests_per_minute: 30,
      };
}

export async function refreshEntitlements(): Promise<Entitlements> {
  try {
    const api = await getEntitlementsMe();
    const limits =
      api.limits && typeof api.limits.max_refresh_items_per_run === "number"
        ? api.limits
        : getLimitsFromPlan(api.effective_plan);
    const updatedAt = api.updated_at ?? nowIso();
    const expiresAt =
      api.expires_at ?? new Date(Date.now() + 600 * 1000).toISOString();
    const ent = normalizeFromApi({
      effective_plan: api.effective_plan,
      effective_status: api.effective_status,
      limits,
      updated_at: updatedAt,
      expires_at: expiresAt,
      current_period_end: api.current_period_end,
      request_id: api.request_id,
      stripe_customer_id: api.stripe_customer_id,
      stripe_subscription_id: api.stripe_subscription_id,
    });
    await storeEntitlements(ent);
    logger.log("Entitlements", "refresh success", "request_id=" + ent.request_id);
    return ent;
  } catch (error) {
    logger.error("Entitlements", "refresh failed", error);
    throw error;
  }
}

export async function getEntitlements(options?: { forceRefresh?: boolean }): Promise<Entitlements> {
  const force = options?.forceRefresh === true;

  if (force) {
    if (inflightForce) {
      return inflightForce;
    }
    const p = refreshEntitlements()
      .then((ent) => {
        inflightForce = null;
        return ent;
      })
      .catch((err) => {
        inflightForce = null;
        throw err;
      });
    inflightForce = p;
    return p;
  }

  if (inflightForce) {
    return inflightForce;
  }

  const cached = await getCachedEntitlements();
  if (cached && !isEntitlementsStale(cached)) {
    return cached;
  }

  if (inflightNonForce) {
    return inflightNonForce;
  }

  const p = (async (): Promise<Entitlements> => {
    try {
      const ent = await refreshEntitlements();
      inflightNonForce = null;
      return ent;
    } catch (err) {
      const cachedOnFail = await getCachedEntitlements();
      if (cachedOnFail && !isEntitlementsStale(cachedOnFail)) {
        logger.log("Entitlements", "refresh failed, using cached", "request_id=" + cachedOnFail.request_id);
        inflightNonForce = null;
        return cachedOnFail;
      }
      inflightNonForce = null;
      logger.log("Entitlements", "refresh failed, no valid cache, returning synthesized Free");
      return synthesizeFree();
    }
  })();

  inflightNonForce = p;
  return p;
}

export function getEffectiveLimits(entitlements: Entitlements): EntitlementsLimits {
  return entitlements.limits;
}

/** Fallback max_chain_rows when entitlements are unavailable (e.g. custom function context). Single source: CONSERVATIVE_LIMITS. */
export function getFallbackMaxChainRows(): number {
  return CONSERVATIVE_LIMITS.max_chain_rows;
}

export function clearEntitlementsCache(): void {
  inflightForce = null;
  inflightNonForce = null;
  const storage = getStorage();
  if (storage) {
    storage.removeItem(STORAGE_KEYS.ENTITLEMENTS).catch(() => {});
  }
}
